import { Box, CircularProgress, Typography } from '@material-ui/core'
import moment from 'moment'
import React, { useMemo } from 'react'
import { FormattedDateTime } from '../formatted-date-time'
import { getAckTimestampForDelay } from '../message-appearance-v2/ackTimestamp'
import { useListMessageAppearanceAcksV2 } from '../message-appearance-v2/hooks/useListMessageAppearanceAcksV2'
import {
  ListMessageAppearanceAcksV2Variables
} from '../message-appearance-v2/hooks/__generated__/ListMessageAppearanceAcksV2'

/** Shown wherever we explain what device message acknowledgment means. */
export const DEVICE_MESSAGE_ACK_DESCRIPTION =
  'If enabled, the device receives a message and sends us an acknowledgment that it received and displayed it.'

const NO_RECORD_HINT =
  'Device message acknowledgments may be disabled, or the device may be offline.'

export interface BoardLastAcknowledgmentProps {
  /** When both `boardId` and `deviceId` are set, `boardId` is used (API expects exactly one). */
  boardId?: string | null
  deviceId?: string | null
  className?: string
}

export const BoardLastAcknowledgment: React.FC<BoardLastAcknowledgmentProps> = ({
  boardId,
  deviceId,
  className
}) => {
  const variables: ListMessageAppearanceAcksV2Variables = useMemo(() => {
    if (boardId) {
      return { input: { boardId, limit: 1, sortDirection: 'desc' as any } }
    }
    if (deviceId) {
      return { input: { deviceId, limit: 1, sortDirection: 'desc' as any } }
    }
    return { input: { boardId: '_', limit: 1, sortDirection: 'desc' as any } }
  }, [boardId, deviceId])

  const skipQuery = !boardId && !deviceId

  const { data, loading, error } = useListMessageAppearanceAcksV2(variables, { skip: skipQuery })

  if (skipQuery) {
    return null
  }

  if (loading && !data) {
    return (
      <Box className={className} display='flex' alignItems='center' minHeight={24}>
        <CircularProgress size={18} />
      </Box>
    )
  }

  if (error) {
    return (
      <Typography variant='body2' color='error' className={className}>
        Could not load device message acknowledgments.
      </Typography>
    )
  }

  const acknowledgments = data?.listMessageAppearanceAcksV2?.acknowledgments ?? []
  const last = acknowledgments[0]

  const captionProps = {
    variant: 'caption' as const,
    color: 'textSecondary' as const,
    display: 'block' as const,
    style: { marginTop: 4 }
  }

  if (!last) {
    return (
      <Box className={className}>
        <Typography variant='body2' component='span'>
          Never
        </Typography>
        <Typography {...captionProps}>{DEVICE_MESSAGE_ACK_DESCRIPTION}</Typography>
        <Typography {...captionProps}>{NO_RECORD_HINT}</Typography>
      </Box>
    )
  }

  const ackTimestamp = getAckTimestampForDelay(last)
  const isOlderThanOneMonth =
    ackTimestamp !== 0 && moment(ackTimestamp).isBefore(moment().subtract(1, 'month'))

  return (
    <Box className={className}>
      <Typography variant='body2' component='span'>
        <FormattedDateTime value={ackTimestamp} />
      </Typography>
      {isOlderThanOneMonth ? (
        <Typography {...captionProps}>
          That was more than a month ago. {NO_RECORD_HINT}
        </Typography>
      ) : null}
    </Box>
  )
}
